/** Панель платформенной модерации (сотрудники, бит 1 в flags): жалобы, аккаунты, журнал. */
import { authed } from './auth'
import type { Snowflake, User } from '@/types/api'

export type ReportStatus = 'open' | 'resolved' | 'rejected'

export interface ModUserRef { id: string; username: string; name: string; avatar: string | null }
export interface ModReport {
  reporter: ModUserRef | null
  resolver: ModUserRef | null
  guild_name: string | null
  channel_name: string | null
  id: Snowflake
  reporter_id: Snowflake
  target_type: 'message' | 'user' | 'guild'
  target_id: Snowflake
  guild_id: Snowflake | null
  channel_id: Snowflake | null
  category: string
  text: string
  snapshot: Record<string, unknown>
  status: ReportStatus
  resolver_id: Snowflake | null
  resolution: string | null
  created_at: string
  resolved_at: string | null
  decision: string | null
  decision_until: string | null
  decision_action_id: string | null
}

export interface ModBan {
  at: string
  until: string | null
  reason: string
}

export interface ModUser {
  user: User
  created_at: string
  flags: number
  banned: ModBan | null
  restricted: { until: string; reason: string } | null
  guilds: number
  reports_against: number
  username_history: { old_username: string; new_username: string; changed_at: string }[]
}

/** Строка списка учётных записей: обзор для владельца. */
export interface ModUserRow {
  id: string
  username: string
  display_name: string | null
  created_at: string
  last_seen: string | null
  status: 'online' | 'idle' | 'dnd' | 'offline'
  flags: number
  has_phone: boolean
  has_email: boolean
  verified_via: string
  guilds: number
  owned: number
  plus: boolean
  banned: boolean
  restricted: boolean
  deleting: string | null
  /** У бота — ник владельца приложения. */
  bot_owner?: string
}

/** Страница людей или ботов; people и bots — сколько всего в обоих списках. */
export interface ModUsersPage {
  total: number
  people: number
  bots: number
  users: ModUserRow[]
}

/** Строка списка серверов. */
export interface ModGuildRow {
  id: string
  name: string
  owner_id: string
  owner_username: string
  created_at: string
  /** Люди, без ботов. */
  members: number
  bots: number
  tier: number
  boosts: number
  listed: boolean
  messages_week: number
}

export interface ModGuildsPage {
  total: number
  guilds: ModGuildRow[]
}

export interface ModAction {
  staff: ModUserRef | null
  target_user: ModUserRef | null
  id: Snowflake
  staff_id: Snowflake
  action: string
  report_id: string | null
  target_user_id: Snowflake | null
  target_id: Snowflake | null
  reason: string
  meta: Record<string, unknown>
  created_at: string
}

/** Цифры для владельца: раздел «Статистика» в панели модерации. Ряды — последние 30 дней по Москве. */
export interface ModStats {
  at: string
  /** Люди; боты — отдельным числом bots и в остальные цифры не входят. */
  users: { total: number; new_day: number; new_week: number; new_month: number; with_email: number; deleted: number; bots: number }
  online: { now: number; peak_day: number }
  active: { day: number; week: number; month: number }
  messages: { day: number; week: number; dm_day: number }
  voice: { now: number; streaming_now: number; minutes_day: number; stream_minutes_day: number; call_minutes_day: number }
  guilds: { total: number; new_week: number; active_week: number; listed: number; top: { id: Snowflake; name: string; messages: number; members: number }[] }
  money: { plus_active: number; boosts: number; paid_month_rub: number; referrals_qualified: number; referrals_week: number; sms_balance: number | null }
  funnel: { started: number; confirmed: number; recover_started: number }
  push_users: number
  /** Сайт за сегодня: заходы считаем сами, регистрации по кампаниям — из базы. */
  site: {
    visits: number
    unique: number
    register_opens: number
    login_opens: number
    campaigns: { code: string; visits: number; signups: number }[] | null
    referrers: { host: string; visits: number }[] | null
  }
  series: { days: string[]; new_users: number[]; messages: number[]; active: number[]; peak_online: number[] }
}

export interface ModDecision { action: 'reject' | 'message_delete' | 'ban' | 'restrict' | 'link'; reason: string; until?: string | null; action_id?: string }
export const MODERATOR_FLAG = 1 << 21
export const isPlatformStaff = (flags: number) => (flags & (1 | MODERATOR_FLAG)) !== 0
export const isPlatformAdmin = (flags: number) => (flags & 1) !== 0
export const moderationApi = {
  decide: (id: string, input: ModDecision) => authed<ModReport>(`/moderation/reports/${id}/decision`, { method: 'POST', body: JSON.stringify(input) }),
  reportActions: (id: string) => authed<ModAction[]>(`/moderation/reports/${id}/actions`),
  userActions: (id: string) => authed<ModAction[]>(`/moderation/users/${id}/actions?limit=100`),
  staff: () => authed<{ user: User; role: 'admin' | 'moderator' }[]>('/moderation/staff'),
  setRole: (id: string, role: 'admin' | 'moderator' | 'none') => authed<ModUser>(`/moderation/users/${id}/staff`, { method: 'PUT', body: JSON.stringify({ role }) }),
  stats: () => authed<ModStats>('/moderation/stats'),
  reports: (status: ReportStatus, before?: string) => authed<ModReport[]>(`/moderation/reports?status=${status}&limit=50${before ? `&before=${before}` : ''}`),
  resolveReport: (id: string, status: 'resolved' | 'rejected', resolution: string) =>
    authed<ModReport>(`/moderation/reports/${id}`, { method: 'PATCH', body: JSON.stringify({ status, resolution }) }),
  /** Учётные записи людей или ботов, новые сверху; q — начало ника или имени. */
  users: (opts: { before?: string | undefined; q?: string | undefined; bots?: boolean | undefined } = {}) =>
    authed<ModUsersPage>(`/moderation/users?limit=50${opts.bots ? '&kind=bots' : ''}${opts.before ? `&before=${opts.before}` : ''}${opts.q ? `&q=${encodeURIComponent(opts.q)}` : ''}`),
  /** Все серверы, новые сверху; q — часть названия. */
  guilds: (opts: { before?: string | undefined; q?: string | undefined } = {}) =>
    authed<ModGuildsPage>(`/moderation/guilds?limit=50${opts.before ? `&before=${opts.before}` : ''}${opts.q ? `&q=${encodeURIComponent(opts.q)}` : ''}`),
  user: (id: string) => authed<ModUser>(`/moderation/users/${id}`),
  findUser: (username: string) => authed<ModUser>(`/moderation/users/search?username=${encodeURIComponent(username)}`),
  ban: (id: string, body: { reason: string; until?: string | null }) => authed<ModUser>(`/moderation/users/${id}/ban`, { method: 'PUT', body: JSON.stringify(body) }),
  unban: (id: string) => authed<ModUser>(`/moderation/users/${id}/ban`, { method: 'DELETE' }),
  restrict: (id: string, body: { reason: string; until: string }) => authed<ModUser>(`/moderation/users/${id}/restriction`, { method: 'PUT', body: JSON.stringify(body) }),
  unrestrict: (id: string) => authed<ModUser>(`/moderation/users/${id}/restriction`, { method: 'DELETE' }),
  deleteMessage: (channelId: string, messageId: string, reason: string) =>
    authed<void>(`/moderation/messages/${channelId}/${messageId}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  actions: (before?: string) => authed<ModAction[]>(`/moderation/actions?limit=50${before ? `&before=${before}` : ''}`),
}
